// src/modules/documents/layout/applyManualGroupOrder.js
//
// defsToRender array'ini kullanıcının manuel grup-içi sıralamasına göre yeniden
// düzenler. Mimari mantık:
//
// - Her doc bir gruba aittir (DOC_NAME_TO_GROUP). Manuel sıra GRUP İÇİNDE.
// - Manuel sıra defsToRender'daki SLOT'ları doldurma sırasını değiştirir
//   (slot pozisyonları KORUNUR — diğer grupların doc'ları orijinal yerinde
//   kalır). Örnek:
//     defsToRender = [A(X), B(Y), C(X), D(X), E(Y), F(Z), G(X)]
//     manualGroupOrder = { X: ['D', 'A', 'C', 'G'] }
//     Sonuç:           [D, B, A, C, E, F, G]
//     (X grubunun slot'ları: 0, 2, 3, 6 — D, A, C, G ile dolduruldu)
//
// - Pin'li doc'lar ve dinamik doc'lar (Kat Planı, Vaziyet Planı, Ground Floor)
//   manuel sıralamadan etkilenmez — orijinal pozisyonlarında kalır.
//
// - useElectricalLayout `tryPlaceInCol` greedy packing yapar; sıra değişimi
//   doğrudan farklı sütun yerleşimine yol açar (kullanıcı doc'u yan sütuna
//   sürüklediğinde otomatik sütun değişir).

import { DOC_NAME_TO_GROUP, FALLBACK_LAYOUT_GROUP } from '../../../constants/projectTypes';

const DYNAMIC_DOC_NAMES = new Set(['Kat Planı', 'Vaziyet Planı', 'Ground Floor']);

function getGroupForDef(def, pinnedDocs) {
  if (!def?.name) return null;
  if (DYNAMIC_DOC_NAMES.has(def.name)) return null;
  if (pinnedDocs && pinnedDocs.has(def.name)) return null;
  return DOC_NAME_TO_GROUP[def.name] || FALLBACK_LAYOUT_GROUP;
}

/**
 * @param {Array<{name: string}>} defsToRender
 * @param {Object<string, string[]>} manualGroupOrder - { groupKey: [docName, ...] }
 * @param {Set<string>} pinnedDocs
 * @returns {Array} Yeniden sıralanmış defs (orijinali mutate etmez)
 */
export function applyManualGroupOrder(defsToRender, manualGroupOrder, pinnedDocs) {
  if (!Array.isArray(defsToRender) || defsToRender.length === 0) return defsToRender;
  if (!manualGroupOrder || Object.keys(manualGroupOrder).length === 0) return defsToRender;

  const result = [...defsToRender];

  // Her grup için: defsToRender'da o gruba ait doc'ların (slot, def) listesi
  const groupSlots = new Map(); // groupKey → [{ slotIndex, def }, ...]
  result.forEach((def, idx) => {
    const gKey = getGroupForDef(def, pinnedDocs);
    if (!gKey) return;
    if (!groupSlots.has(gKey)) groupSlots.set(gKey, []);
    groupSlots.get(gKey).push({ slotIndex: idx, def });
  });

  for (const [gKey, slots] of groupSlots) {
    const manualOrder = manualGroupOrder[gKey];
    if (!manualOrder || manualOrder.length === 0) continue;
    // slots'taki doc'ları manuel order'a göre sırala
    // Manuel order'da olmayan doc'lar orijinal sıralarını koruyup sona itilir.
    const orderIdx = new Map(manualOrder.map((name, i) => [name, i]));
    const sortedDocs = slots
      .map((s, originalIdx) => ({ def: s.def, originalIdx }))
      .sort((a, b) => {
        const ai = orderIdx.has(a.def.name) ? orderIdx.get(a.def.name) : Infinity;
        const bi = orderIdx.has(b.def.name) ? orderIdx.get(b.def.name) : Infinity;
        if (ai !== bi) return ai - bi;
        return a.originalIdx - b.originalIdx; // stable for ties
      })
      .map(x => x.def);
    // Slot pozisyonlarına yerleştir
    slots.forEach((s, i) => { result[s.slotIndex] = sortedDocs[i]; });
  }

  return result;
}
